import React, {Fragment, useContext, useEffect} from 'react';
import {ApiUtility} from "../common/ApiUtility";
import {OverlayLoadingIndicator} from "../common/OverlayLoadingIndicator";
import {ListPaginationOptions} from "../common/ListPaginationOptions";
import {CreateUser} from "./CreateUser";
import {UpdateUser} from "./UpdateUser";
import {GlobalContext} from "../admin-frame/AdminFrame";
import {CommonListPageHeader} from "../common/CommonListPageHeader";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {useRenderableData} from "../common/hooks/useRenderableData";
import {UrlUtility} from "../common/UrlUtility";
import {MessageDisplayerUtility} from "../common/MessageDisplayerUtility";

export const UsersListPage = function (props) {
    const context = useContext(GlobalContext);
    const urlBase = '/users';
    const renderableData = useRenderableData({
        selectedPage: 1,
        searchText: '',
        showCreateModal: false,
        showUpdateModal: false,
        updateUserId: null,
        userList: [],
        pageCount: 0,
        loading: false,

        prevUrlQueryString: null,
        prevParams: null,
        prevShowCreateModal: null
    })

    // Update state if URL changes.
    if (
        (props.location && props.location.search) !== renderableData.prevUrlQueryString ||
        (props.match && props.match.params) !== renderableData.prevParams ||
        props.showCreateModal !== renderableData.prevShowCreateModal
    ) {
        let selectedPage = 1;
        let searchText = '';
        let updateUserId = null;
        let showUpdateModal = false;

        let routeParams = props.match && props.match.params;
        if (routeParams && routeParams['userId'] && routeParams['userId'] !== 'add' && props.match.path.indexOf('/add/') <= -1) {
            showUpdateModal = true;
            updateUserId = routeParams['userId'];
        }

        let queryParams = UrlUtility.getQueryParamsFromProps(props);
        if (queryParams.page) {
            selectedPage = queryParams.page;
        }

        if (queryParams.searchText) {
            searchText = queryParams.searchText;
        }

        renderableData.updateAndRender({
            selectedPage: selectedPage,
            searchText: searchText,
            showCreateModal: props.showCreateModal || false,
            showUpdateModal: showUpdateModal || false,
            updateUserId: updateUserId,

            prevUrlQueryString: props.location && props.location.search,
            prevParams: props.match && props.match.params,
            prevShowCreateModal: props.showCreateModal
        })
    }

    // Trigger reload of list data on mount and if certain values change in state.
    useEffect(
        function () {
            reloadList();
        },
        [
            '' + renderableData.selectedPage,
            '' + renderableData.searchText,
            JSON.stringify(renderableData.filters)
        ]
    );

    function reloadList() {
        renderableData.updateAndRender({loading: true})
        ApiUtility.getUsersList(
            renderableData.selectedPage,
            renderableData.searchText,
            renderableData.filters
        )
            .then(function (listData) {
                renderableData.updateAndRender({
                    ...listData,
                    loading: false
                });
            })
            .catch(function (error) {
                renderableData.updateAndRender({loading: false});
                console && console.error(error);
                MessageDisplayerUtility.error(`An error occurred while loading the user list.`);
            });
    }

    function goToAddModal() {
        context.navigator.navigateTo(`${urlBase}/add${props.location.search}`);
    }

    function goToUpdateModal(user) {
        context.navigator.navigateTo(`${urlBase}/${user.id}${props.location.search}`);
    }

    function closeModals() {
        context.navigator.navigateTo(`${urlBase}${props.location.search}`);
    }

    function afterSuccessfulSave() {
        closeModals()
        reloadList();
    }

    function performSearch(searchText) {
        if (renderableData.searchText !== searchText) {
            let queryParams = UrlUtility.getQueryParamsFromProps(props);
            queryParams.searchText = searchText;
            let queryString = UrlUtility.getUrlQueryStringFromQueryParamsObject(queryParams);
            context.navigator.navigateTo(`${urlBase}${queryString}`);
        }
    }

    function renderUsersListDisplay(usersList) {
        let usersListDisplay = [];

        for (let i = 0; i < usersList.length; i++) {
            const user = usersList[i];

            usersListDisplay.push(
                <div
                    key={i + 1}
                    className="common-list-row common-list-values-row"
                    onClick={() => goToUpdateModal(user)}
                >
                    <div className="common-list-row-content">
                        <div
                            className="common-list-row-cell common-list-row-value-cell users-list-row-cell email"
                        >{user.person.email}</div>
                        <div
                            className="common-list-row-cell common-list-row-value-cell users-list-row-cell full-name"
                        >{user.person.name}</div>
                        <div
                            className="common-list-row-cell common-list-row-value-cell users-list-row-cell role"
                        >{user.user_roles[0].role.name}</div>
                    </div>
                </div>
            );
        }

        return usersListDisplay;
    }

    if (renderableData.userList) {
        return (
            <div className="common-list-page users-list-page">
                <CommonListPageHeader
                    headerText="Users"
                    performSearch={performSearch}
                    searchText={renderableData.searchText}
                    searchPlaceholderText="Search Users"
                    onClickAddButton={goToAddModal}
                />

                <div className="common-list-page-content">
                    <ConditionalRenderer
                        if={renderableData.loading}
                        renderer={() => <OverlayLoadingIndicator/>}
                    />
                    {(() => {
                        if (renderableData.userList.length > 0) {
                            return (
                                <Fragment>
                                    <div className="common-list-header">
                                        <div className="common-list-row common-list-labels-row">
                                            <div className="common-list-row-content">
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell users-list-row-cell email"
                                                >Email
                                                </div>
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell users-list-row-cell full-name"
                                                >Name
                                                </div>
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell users-list-row-cell role"
                                                >Role
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="common-list-content">
                                        {renderUsersListDisplay(renderableData.userList)}
                                    </div>
                                    <ListPaginationOptions
                                        pageCount={renderableData.pageCount}
                                        selectedPage={renderableData.selectedPage}
                                        urlBase="/users"
                                        location={props.location}
                                    />
                                </Fragment>
                            );
                        } else {
                            return (
                                <div className="common-empty-list-message-container">
                                    <div className="common-empty-list-message">No users found.</div>
                                </div>
                            );
                        }
                    })()}
                </div>
                {(() => {
                    if (renderableData.showCreateModal) {
                        return (
                            <CreateUser
                                cancel={closeModals}
                                afterSuccessfulSave={afterSuccessfulSave}
                            />
                        );
                    } else if (renderableData.showUpdateModal) {
                        return (
                            <UpdateUser
                                cancel={closeModals}
                                afterSuccessfulSave={afterSuccessfulSave}
                                userId={renderableData.updateUserId}
                            />
                        );
                    }
                })()}
            </div>
        );
    } else {
        return (
            <OverlayLoadingIndicator/>
        );
    }
};
